import { useSite } from "@/context/SiteContext";
import { resolveImage } from "@/lib/siteDefaults";
import { Instagram, ExternalLink } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1523358959537-cc0e4103d0b7?crop=entropy&cs=srgb&fm=jpg&w=800&q=70";

export default function InstagramFeed() {
  const { site } = useSite();
  const ig = site.instagram || {};
  const handle = (ig.handle || "").replace(/^@/, "");
  const posts = (ig.posts || []).filter((p) => p && p.image).slice(0, 9);
  const profileUrl = handle ? `https://instagram.com/${handle}` : "https://instagram.com";
  const hasPosts = posts.length > 0;

  return (
    <section data-testid="instagram-section" className="bg-[#F9F9F6] py-24 md:py-32">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-stone-500 font-semibold mb-4 inline-flex items-center gap-2">
              <Instagram strokeWidth={1.5} className="w-3.5 h-3.5" /> {ig.overline || "Follow along"}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-[#1A1C1A]">
              {ig.heading_line_1 || "Live from the"} <em className="italic text-stone-600">{ig.heading_em || "collective"}</em>{ig.heading_line_3 || "."}
            </h2>
            {ig.subtitle && (
              <p className="mt-4 text-stone-600 font-light max-w-md leading-relaxed">{ig.subtitle}</p>
            )}
          </div>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-cta"
            className="inline-flex items-center gap-2 self-start md:self-auto border border-[#1A1C1A] text-[#1A1C1A] hover:bg-[#1A1C1A] hover:text-[#F9F9F6] px-6 py-3 rounded-full font-sans text-sm tracking-wide transition-all"
          >
            <Instagram strokeWidth={1.5} className="w-4 h-4" />
            {ig.cta_label || `Follow @${handle || "us"}`}
          </a>
        </div>

        {hasPosts ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {posts.map((p, i) => (
              <a
                key={i}
                href={p.url || profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-post-${i}`}
                className="group relative aspect-square overflow-hidden rounded-xl bg-[#F4F1EA]"
              >
                <img
                  src={resolveImage(p.image)}
                  alt={p.caption || `Post ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[700ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 md:p-5">
                  <div className="text-white">
                    {p.caption && (
                      <p className="text-xs md:text-sm leading-snug line-clamp-3 font-light">{p.caption}</p>
                    )}
                    <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em]">
                      <Instagram strokeWidth={1.75} className="w-3 h-3" /> View on Instagram
                    </div>
                  </div>
                </div>
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/85 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ExternalLink strokeWidth={1.75} className="w-3.5 h-3.5 text-[#1A1C1A]" />
                </div>
              </a>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-[#F4F1EA] border border-[#EBE8E0] flex items-center justify-center opacity-60">
                <Instagram strokeWidth={1} className="w-8 h-8 text-stone-400" />
              </div>
            ))}
            <p className="col-span-2 md:col-span-3 text-center text-xs uppercase tracking-[0.2em] text-stone-500 pt-2">
              Add posts from Studio → Site Content → Instagram
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
